/// <reference types="vite/client" />

import type { User, Company } from '../types';

type FilterOp = 'eq' | 'is';
type Filter = { op: FilterOp; column: string; value: unknown };
type OrderBy = { column: string; ascending: boolean };

interface QueryState {
  table: string;
  action: 'select' | 'insert' | 'upsert' | 'update' | 'delete';
  selectColumns?: string;
  returningColumns?: string;
  filters: Filter[];
  orderBy?: OrderBy;
  limit?: number;
  offset?: number;
  wantExactCount?: boolean;
  payload?: unknown;
  upsertOptions?: { onConflict?: string };
}

interface ApiResult<T> {
  data: T | null;
  error: Error | null;
  /** Total de linhas quando a query usou count=exact. */
  total?: number;
}

interface SessionPayload {
  user?: { id: string; username: string };
}

/** Perfil completo retornado por GET /auth/session (Sprint 6). */
export interface SessionProfile {
  user: User;
  activeCompany: Company | null;
}

/** Chaves legadas no sessionStorage — removidas na migração para cookie httpOnly + API. */
const LEGACY_SESSION_TOKEN_STORAGE_KEY = 'rh_gestao.auth.access_token';
const LEGACY_SESSION_COMPANY_KEY = 'rh_gestao.auth.company';
const LEGACY_SESSION_USER_KEY = 'rh_gestao.auth.user';

/**
 * Remove chaves legadas do sessionStorage (migrações S2/S6).
 */
function clearLegacyStoredSessionKeys(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(LEGACY_SESSION_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_COMPANY_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_USER_KEY);
  } catch {
    // ignore
  }
}

class QueryBuilder<T = any> {
  private state: QueryState;
  private readonly client: PostgresApiClient;

  constructor(client: PostgresApiClient, table: string) {
    this.client = client;
    this.state = { table, action: 'select', filters: [] };
  }

  select(columns = '*') {
    if (this.state.action === 'insert' || this.state.action === 'upsert' || this.state.action === 'update' || this.state.action === 'delete') {
      this.state.returningColumns = columns;
    } else {
      this.state.action = 'select';
      this.state.selectColumns = columns;
    }
    return this as QueryBuilder<T>;
  }

  eq(column: string, value: unknown) {
    this.state.filters.push({ op: 'eq', column, value });
    return this;
  }

  is(column: string, value: unknown) {
    this.state.filters.push({ op: 'is', column, value });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.state.orderBy = { column, ascending: options?.ascending !== false };
    return this;
  }

  limit(limit: number) {
    this.state.limit = limit;
    return this;
  }

  /** Deslocamento para paginação (pares com limit). */
  offset(offset: number) {
    this.state.offset = offset;
    return this;
  }

  /** Pede total de linhas na resposta (count=exact). */
  count(mode: 'exact') {
    if (mode === 'exact') this.state.wantExactCount = true;
    return this;
  }

  insert(payload: unknown) {
    this.state.action = 'insert';
    this.state.payload = payload;
    return this as QueryBuilder<T>;
  }

  upsert(payload: unknown, options?: { onConflict?: string }) {
    this.state.action = 'upsert';
    this.state.payload = payload;
    this.state.upsertOptions = options;
    return this as QueryBuilder<T>;
  }

  update(payload: unknown) {
    this.state.action = 'update';
    this.state.payload = payload;
    return this as QueryBuilder<T>;
  }

  delete() {
    this.state.action = 'delete';
    return this as QueryBuilder<T>;
  }

  maybeSingle(): Promise<ApiResult<T>> {
    return this.client.execute<T>(this.state, 'maybeSingle');
  }

  single(): Promise<ApiResult<T>> {
    return this.client.execute<T>(this.state, 'single');
  }

  then<TResult1 = ApiResult<T[]>, TResult2 = never>(
    onfulfilled?: ((value: ApiResult<T[]>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.client.execute<T[]>(this.state, 'many').then(onfulfilled as any, onrejected as any);
  }
}

export class PostgresApiClient {
  private readonly baseUrl: string;
  /** Perfil de sessão enriquecido (permissões + empresa ativa). */
  private sessionProfile: SessionProfile | null = null;
  /** Usuário mínimo da sessão validada em memória. */
  private sessionUser: { id: string; username: string } | null = null;
  /** Evita revalidar /auth/session a cada chamada na mesma carga de página. */
  private sessionValidated = false;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    clearLegacyStoredSessionKeys();
  }

  /**
   * Marca sessão como válida após login ou /auth/session bem-sucedido.
   * @param user - Usuário autenticado retornado pela API
   */
  private setSessionUser(user: { id: string; username: string }): void {
    this.sessionUser = user;
    this.sessionValidated = true;
  }

  /**
   * Limpa estado de sessão local após logout ou sessão inválida no servidor.
   */
  private clearSession(): void {
    this.sessionUser = null;
    this.sessionProfile = null;
    this.sessionValidated = false;
    clearLegacyStoredSessionKeys();
  }

  /**
   * Carrega perfil completo via GET /auth/session (permissões + empresa ativa).
   * @returns Perfil ou null se não autenticado
   */
  async getSessionProfile(): Promise<{ data: SessionProfile | null; error: Error | null }> {
    if (this.sessionValidated && this.sessionProfile) {
      return { data: this.sessionProfile, error: null };
    }

    try {
      const response = await this.request('/auth/session', { method: 'GET' });
      if (!response.ok) {
        this.clearSession();
        return { data: null, error: null };
      }

      const payload = (await response.json()) as {
        session?: { user?: User; activeCompany?: Company | null } | null;
      };

      if (!payload.session?.user) {
        this.clearSession();
        return { data: null, error: null };
      }

      this.sessionProfile = {
        user: payload.session.user,
        activeCompany: payload.session.activeCompany ?? null,
      };
      this.sessionUser = {
        id: payload.session.user.id,
        username: payload.session.user.username,
      };
      this.sessionValidated = true;
      return { data: this.sessionProfile, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Persiste empresa ativa no servidor (rh_sessions.active_company_id).
   * @param company - Nome da unidade selecionada
   */
  async setActiveCompany(company: Company): Promise<{ error: Error | null }> {
    try {
      const response = await this.request('/auth/active-company', {
        method: 'POST',
        body: JSON.stringify({ company }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        return {
          error: new Error(
            typeof payload?.error === 'string' ? payload.error : 'Falha ao definir empresa ativa',
          ),
        };
      }

      if (this.sessionProfile) {
        this.sessionProfile.activeCompany = company;
      }

      return { error: null };
    } catch (error) {
      return {
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  from<T = any>(table: string): QueryBuilder<T> {
    return new QueryBuilder<T>(this, table);
  }

  get auth() {
    return {
      signInWithPassword: async ({ email, password }: { email: string; password: string }) => {
        try {
          const response = await this.request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username: email, password }),
          });
          const payload = (await response.json()) as { session?: SessionPayload };
          if (!response.ok || !payload.session?.user) {
            return { data: { user: null }, error: new Error('Credenciais inválidas') };
          }
          this.setSessionUser(payload.session.user);
          return {
            data: { user: payload.session.user },
            error: null,
          };
        } catch (error: any) {
          return { data: { user: null }, error };
        }
      },
      /**
       * Valida sessão via cookie httpOnly no servidor (/auth/session).
       * Preferir getSessionProfile() quando precisar de permissões e empresa ativa.
       */
      getSession: async () => {
        const { data, error } = await this.getSessionProfile();
        if (error) return { data: { session: null } };
        if (data?.user) {
          return { data: { session: { user: data.user } } };
        }
        return { data: { session: null } };
      },
      /**
       * Troca a senha do usuário autenticado (valida senha atual no servidor).
       * @param currentPassword - Senha em uso
       * @param newPassword - Nova senha (mín. 8 caracteres)
       */
      changePassword: async (currentPassword: string, newPassword: string) => {
        try {
          const response = await this.request('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({ currentPassword, newPassword }),
          });
          const payload = (await response.json()) as { ok?: boolean; error?: string };
          if (!response.ok) {
            return {
              ok: false as const,
              error: typeof payload?.error === 'string' ? payload.error : 'Falha ao alterar senha',
            };
          }
          return { ok: true as const, error: null };
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          return { ok: false as const, error: message };
        }
      },
      /** Encerra sessão no servidor (revoga token) e limpa estado local. */
      signOut: async () => {
        try {
          await this.request('/auth/logout', { method: 'POST' });
        } catch {
          // Mesmo com falha de rede, limpa estado local
        } finally {
          this.clearSession();
        }
        return { error: null };
      },
    };
  }

  async execute<T>(state: QueryState, returnType: 'many' | 'single' | 'maybeSingle'): Promise<ApiResult<T>> {
    try {
      const tablePath = `/db/${encodeURIComponent(state.table)}`;
      let response: Response;

      if (state.action === 'select') {
        const params = new URLSearchParams();
        if (state.selectColumns) params.set('select', state.selectColumns);
        state.filters.forEach((f) => params.append(`${f.op}.${f.column}`, String(f.value)));
        if (state.orderBy) params.set('order', `${state.orderBy.column}.${state.orderBy.ascending ? 'asc' : 'desc'}`);
        if (typeof state.limit === 'number') params.set('limit', String(state.limit));
        if (typeof state.offset === 'number') params.set('offset', String(state.offset));
        if (state.wantExactCount) params.set('count', 'exact');
        response = await this.request(`${tablePath}?${params.toString()}`, { method: 'GET' });
      } else if (state.action === 'insert') {
        response = await this.request(`${tablePath}/insert`, {
          method: 'POST',
          body: JSON.stringify({ rows: state.payload, returning: state.returningColumns ?? (returnType !== 'many' ? '*' : null) }),
        });
      } else if (state.action === 'upsert') {
        response = await this.request(`${tablePath}/upsert`, {
          method: 'POST',
          body: JSON.stringify({
            rows: state.payload,
            onConflict: state.upsertOptions?.onConflict,
            returning: state.returningColumns ?? (returnType !== 'many' ? '*' : null),
          }),
        });
      } else if (state.action === 'update') {
        response = await this.request(`${tablePath}`, {
          method: 'PATCH',
          body: JSON.stringify({ values: state.payload, filters: state.filters, returning: state.returningColumns ?? null }),
        });
      } else {
        response = await this.request(`${tablePath}`, {
          method: 'DELETE',
          body: JSON.stringify({ filters: state.filters, returning: state.returningColumns ?? null }),
        });
      }

      const payload = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        const msg =
          typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
        console.error('[Postgres API]', state.action, state.table, msg);
        return { data: null, error: new Error(msg) };
      }

      const rows = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
      const total = typeof payload?.total === 'number' ? payload.total : undefined;
      if (returnType === 'single') {
        if (rows.length !== 1) {
          return { data: null, error: new Error('Registro único não encontrado') };
        }
        return { data: rows[0] as T, error: null, total };
      }
      if (returnType === 'maybeSingle') {
        return { data: (rows[0] ?? null) as T, error: null, total };
      }
      return { data: rows as T, error: null, total };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  async callAdminUserAction(action: 'create' | 'update' | 'delete', payload: { email?: string; password?: string; userId?: string }) {
    const response = await this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify({ action, ...payload }),
    });
    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok) {
      const msg = typeof data?.error === 'string' ? data.error : `Erro HTTP ${response.status}`;
      return { error: msg };
    }
    return data;
  }

  /**
   * Carrega usuários com empresas vinculadas em query única (JOIN).
   * @returns Linhas planas: campos de rh_users + company_name (nullable)
   */
  async fetchUsersWithCompanies(): Promise<ApiResult<(Record<string, unknown> & { company_name?: string | null })[]>> {
    try {
      const response = await this.request('/queries/users-with-companies', { method: 'GET' });
      const payload = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        const msg = typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
        return { data: null, error: new Error(msg) };
      }
      return { data: Array.isArray(payload?.data) ? payload.data : [], error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  }

  /**
   * Popula estrutura inicial da empresa (colunas, roles, cost_centers) via servidor.
   * @param companyId - UUID da empresa
   */
  async seedCompanyStructure(
    companyId: string,
  ): Promise<ApiResult<{ ok: boolean; seeded?: boolean }>> {
    try {
      const response = await this.request('/queries/seed-company-structure', {
        method: 'POST',
        body: JSON.stringify({ company_id: companyId }),
      });
      const payload = await response.json().catch(() => ({} as Record<string, unknown>));
      if (!response.ok) {
        const msg = typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
        return { data: null, error: new Error(msg) };
      }
      return { data: { ok: Boolean(payload?.ok), seeded: Boolean(payload?.seeded) }, error: null };
    } catch (error: unknown) {
      return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  /**
   * Carrega ausências de uma empresa via JOIN com rh_employees (sem full scan).
   * @param companyId - UUID da empresa
   */
  async fetchAbsencesByCompany(companyId: string): Promise<ApiResult<Record<string, unknown>[]>> {
    return this.fetchAllQueryPages('/queries/absences-by-company', { company_id: companyId });
  }

  /**
   * Carrega situações de colaboradores de uma empresa via JOIN com rh_employees.
   * @param companyId - UUID da empresa
   */
  async fetchSituationsByCompany(companyId: string): Promise<ApiResult<Record<string, unknown>[]>> {
    return this.fetchAllQueryPages('/queries/situations-by-company', { company_id: companyId });
  }

  /**
   * Carrega atrasos de colaboradores de uma empresa via JOIN com rh_employees.
   * @param companyId - UUID da empresa
   */
  async fetchDelaysByCompany(companyId: string): Promise<ApiResult<Record<string, unknown>[]>> {
    return this.fetchAllQueryPages('/queries/delays-by-company', { company_id: companyId });
  }

  /**
   * Carrega saídas antecipadas de colaboradores de uma empresa via JOIN com rh_employees.
   * @param companyId - UUID da empresa
   */
  async fetchEarlyDeparturesByCompany(
    companyId: string,
  ): Promise<ApiResult<Record<string, unknown>[]>> {
    return this.fetchAllQueryPages('/queries/early-departures-by-company', {
      company_id: companyId,
    });
  }

  /**
   * Verifica se o board mudou desde o último sync (ETag / 304).
   * @param companyId - UUID da empresa
   * @param etag - ETag anterior; envia If-None-Match quando presente
   */
  async fetchEmployeesBoardVersion(
    companyId: string,
    etag?: string | null,
  ): Promise<
    | { ok: true; unchanged: true; etag: string }
    | { ok: true; unchanged: false; etag: string }
    | { ok: false; error: Error }
  > {
    try {
      const headers: Record<string, string> = {};
      if (etag) headers['If-None-Match'] = `"${etag}"`;
      const params = new URLSearchParams({ company_id: companyId });
      const response = await this.request(`/queries/employees-board-version?${params.toString()}`, {
        method: 'GET',
        headers,
      });
      if (response.status === 304) {
        const headerEtag = response.headers.get('ETag')?.replace(/"/g, '') ?? etag ?? '';
        return { ok: true, unchanged: true, etag: headerEtag };
      }
      const payload = await response.json().catch(() => ({} as Record<string, unknown>));
      if (!response.ok) {
        const msg = typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
        return { ok: false, error: new Error(msg) };
      }
      return { ok: true, unchanged: false, etag: String(payload.etag ?? '') };
    } catch (error: unknown) {
      return { ok: false, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  /**
   * Substitui todos os registros de um colaborador em uma tabela de ocorrências dentro de uma
   * transação Postgres atômica (DELETE + INSERT). Evita perda de dados quando o INSERT falha
   * após um DELETE bem-sucedido em chamadas HTTP separadas.
   * @param table - Nome da tabela (rh_absences | rh_delays | rh_early_departures | rh_situations)
   * @param employeeId - UUID do colaborador
   * @param rows - Linhas a inserir (pode ser vazio para apenas deletar)
   */
  async replaceEmployeeRecords(
    table: string,
    employeeId: string,
    rows: Record<string, unknown>[],
  ): Promise<{ error: Error | null }> {
    try {
      const response = await this.request('/queries/replace-employee-records', {
        method: 'POST',
        body: JSON.stringify({ employee_id: employeeId, table, rows }),
      });
      const payload = await response.json().catch(() => ({} as Record<string, unknown>));
      if (!response.ok) {
        const msg = typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
        return { error: new Error(msg) };
      }
      return { error: null };
    } catch (error: unknown) {
      return { error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  /**
   * Busca todas as páginas de uma rota /queries/* com limit/offset automático.
   * @param path - Caminho da query (ex: /queries/absences-by-company)
   * @param baseParams - Parâmetros fixos (ex: company_id)
   */
  private async fetchAllQueryPages(
    path: string,
    baseParams: Record<string, string>,
  ): Promise<ApiResult<Record<string, unknown>[]>> {
    const PAGE_SIZE = 200;
    const all: Record<string, unknown>[] = [];
    let offset = 0;
    let total = Infinity;

    try {
      while (offset < total) {
        const params = new URLSearchParams({
          ...baseParams,
          limit: String(PAGE_SIZE),
          offset: String(offset),
          count: 'exact',
        });
        const response = await this.request(`${path}?${params.toString()}`, { method: 'GET' });
        const payload = await response.json().catch(() => ({} as Record<string, unknown>));
        if (!response.ok) {
          const msg = typeof payload?.error === 'string' ? payload.error : `Erro HTTP ${response.status}`;
          return { data: null, error: new Error(msg) };
        }
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        all.push(...rows);
        total = typeof payload?.total === 'number' ? payload.total : rows.length;
        offset += rows.length;
        if (rows.length === 0) break;
      }
      return { data: all, error: null, total: all.length };
    } catch (error: unknown) {
      return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  /**
   * Dispara envio de teste WhatsApp via backend (credenciais Evolution não saem do bundle).
   * @param payload - Número, mensagem opcional e credenciais do formulário (mescladas com rh_settings no servidor)
   */
  async testWhatsApp(payload: {
    number: string;
    message?: string;
    baseUrl?: string;
    apiKey?: string;
    instance?: string;
  }): Promise<{ ok: boolean; error?: string }> {
    try {
      const response = await this.request('/integrations/whatsapp/test', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({} as Record<string, unknown>));
      if (!response.ok) {
        const msg = typeof body?.error === 'string' ? body.error : `Erro HTTP ${response.status}`;
        return { ok: false, error: msg };
      }
      return { ok: true };
    } catch (error: unknown) {
      const raw = error instanceof Error ? error.message : 'Falha no teste de envio';
      const msg =
        raw === 'Failed to fetch' || /network|fetch/i.test(raw)
          ? 'Não foi possível contactar a API (rede, CORS ou servidor indisponível). Verifique a URL da API e CORS_ORIGINS no EasyPanel.'
          : raw;
      return { ok: false, error: msg };
    }
  }

  private request(path: string, init: RequestInit): Promise<Response> {
    return fetch(`${this.baseUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
  }
}

let _client: PostgresApiClient | null = null;
let _currentUrl = '';

/**
 * Evita Mixed Content: páginas HTTPS não podem chamar `http://`.
 * Se a página está em HTTPS e a base da API veio como HTTP (exceto localhost), usa HTTPS.
 * @param url - URL base já resolvida (pode ser vazia para mesmo-origem)
 * @returns URL ajustada quando o browser exige TLS na sub-requisição
 */
function normalizePostgresApiBaseForPage(url: string): string {
  if (!url || typeof window === 'undefined' || window.location.protocol !== 'https:') {
    return url;
  }
  if (!/^http:\/\//i.test(url)) {
    return url;
  }
  try {
    const host = new URL(url).hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return url;
    }
  } catch {
    return url;
  }
  return url.replace(/^http:/i, 'https:');
}

/** URL explícita (settings) ou env; em produção ignora localhost embutido no build (usa mesma origem). */
function resolvePostgresApiUrl(explicitUrl: string | undefined): string {
  const fromEnv = import.meta.env.VITE_POSTGRES_API_URL || '';
  const trimmedExplicit = explicitUrl?.trim();
  const raw = trimmedExplicit ? trimmedExplicit : fromEnv;
  let out: string;
  if (import.meta.env.PROD && raw && (raw.includes('localhost') || raw.includes('127.0.0.1'))) {
    out = '';
  } else {
    out = raw;
  }
  return normalizePostgresApiBaseForPage(out);
}

/**
 * Base da API para `fetch` na UI (health, webhooks, etc.): mesma regra do client + fallback na origem atual.
 * @param explicitUrlFromSettings - URL salva em Configurações; se vazia, usa env / mesma origem
 * @returns URL sem barra final
 */
export function getPostgresApiBaseUrl(explicitUrlFromSettings: string | undefined): string {
  const resolved = resolvePostgresApiUrl(explicitUrlFromSettings);
  let base = resolved.replace(/\/+$/, '');
  if (!base && typeof window !== 'undefined') {
    base = window.location.origin.replace(/\/+$/, '');
  }
  return normalizePostgresApiBaseForPage(base);
}

export function getPostgresClient(url?: string): PostgresApiClient {
  // URL vazia após resolver = mesmo-origem: Express serve API e frontend na mesma porta (produção).
  // Em dev local, VITE_POSTGRES_API_URL aponta para http://localhost:4000.
  const finalUrl = resolvePostgresApiUrl(url);

  if (_client === null || finalUrl !== _currentUrl) {
    _currentUrl = finalUrl;
    _client = new PostgresApiClient(finalUrl);
  }
  return _client;
}
